/**
 * SubscriptionPage.js
 * ====================
 * 
 * Page de souscription individuelle.
 * 
 * RÈGLE MOA STRICTE:
 * - ❌ Aucun prix hardcodé
 * - ❌ Aucun fallback de prix
 * - ✅ Tous les prix viennent du backend (Administration → Tarification)
 */

import React, { useState, useEffect } from 'react';
import { safeFetchJSON } from '../utils/apiHelpers';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';

import { getBackendUrl, getApiUrl } from '../config/environment';
const BACKEND_URL = getBackendUrl();

function SubscriptionPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pricingData, setPricingData] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(true);
  const [pricingError, setPricingError] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Simple: Always return to training dashboard
  const handleBack = () => {
    console.log('[SubscriptionPage] Returning to training dashboard');
    navigate('/subscription');
  };

  // Fetch dynamic pricing from backend on component mount
  // ❌ NO FALLBACK - pricing MUST come from backend (Admin → Tarification)
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/billing/public/premium`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.price !== undefined) {
            setPricingData(data);
            setPricingError(false);
          } else {
            console.error('[SubscriptionPage] Invalid pricing data from backend');
            setPricingError(true);
            setPricingData(null);
          }
        } else {
          console.error('[SubscriptionPage] Failed to fetch pricing - backend error');
          setPricingError(true);
          setPricingData(null);
        }
      } catch (err) {
        console.error('[SubscriptionPage] Error fetching pricing:', err);
        setPricingError(true);
        setPricingData(null);
      } finally {
        setLoadingPrice(false);
      }
    };

    fetchPricing();
  }, []);

  const handleSubscribe = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Get current origin URL
      const originUrl = window.location.origin;

      // Call backend to create checkout session using safeFetchJSON
      const token = localStorage.getItem('token');
      const data = await safeFetchJSON(`${BACKEND_URL}/api/billing/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          origin_url: originUrl,
          plan_id: 'premium'
        })
      });

      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('URL de paiement non reçue');
      }

    } catch (err) {
      console.error('Erreur de paiement:', err);
      setError(err.message || 'Une erreur est survenue');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        {/* Back Button - Fixed position on mobile and desktop */}
        <button
          onClick={handleBack}
          className="fixed top-4 left-4 md:absolute md:top-0 md:left-0 z-50 flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white transition-all duration-300 hover:scale-105 group shadow-lg"
          aria-label="Retour au tableau de bord"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span className="hidden sm:inline font-medium">Retour au Dashboard</span>
          <span className="sm:hidden font-medium">Retour</span>
        </button>

        {/* Header */}
        <div className="text-center mb-12 mt-16 md:mt-0">
          <h1 className="text-5xl font-bold text-white mb-4">
            Passez au <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Premium</span>
          </h1>
          <p className="text-xl text-gray-300">
            Débloquez tout le potentiel du Coach DIA
          </p>
        </div>

        {/* Loading State */}
        {loadingPrice ? (
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 shadow-2xl text-center">
            <div className="flex items-center justify-center space-x-3">
              <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
              <p className="text-white text-lg">Chargement des tarifs...</p>
            </div>
          </div>
        ) : pricingError || !pricingData ? (
          /* Error State - NO FALLBACK PRICE */
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-red-500/30 shadow-2xl text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-white font-semibold text-xl mb-2">Tarification indisponible</h3>
            <p className="text-gray-400 mb-6">
              Les tarifs ne peuvent pas être chargés actuellement. Veuillez réessayer plus tard.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-medium transition-colors"
            >
              Réessayer
            </button>
          </div>
        ) : (
          /* Pricing Card - ALL PRICES FROM BACKEND */
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 shadow-2xl">
            <div className="text-center mb-8">
              {/* Promotional pricing display */}
              {pricingData.has_promotion ? (
                <div>
                  {/* Original price (crossed out) and discount badge */}
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <span className="text-2xl text-gray-400 line-through">
                      {pricingData.original_price.toFixed(2)}€
                    </span>
                    {pricingData.savings && (
                      <span className="inline-block bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                        -{pricingData.savings.toFixed(2)}€
                      </span>
                    )}
                  </div>
                  
                  {/* Current price (large) - FROM BACKEND ONLY */}
                  <div className="text-6xl font-bold text-white mb-2">
                    {pricingData.price.toFixed(2)}€
                    <span className="text-2xl text-gray-400">/mois</span>
                  </div>
                  
                  {/* Promotion name - FROM BACKEND */}
                  {pricingData.promotion_name && (
                    <p className="text-amber-400 font-semibold text-lg mb-2">
                      {pricingData.promotion_name}
                    </p>
                  )}
                </div>
              ) : (
                /* No promotion - standard display - FROM BACKEND ONLY */
                <div className="text-6xl font-bold text-white mb-2">
                  {pricingData.price.toFixed(2)}€
                  <span className="text-2xl text-gray-400">/mois</span>
                </div>
              )}
              
              <p className="text-amber-400 font-medium">
                ✨ Essai gratuit de 1 jour inclus
              </p>
            </div>

          {/* Features */}
          <div className="space-y-4 mb-8">
            <div className="flex items-start space-x-3">
              <svg className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="text-white font-semibold">20 Modules Premium</h3>
                <p className="text-gray-400 text-sm">Accès complet à tous les modules de formation executive</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <svg className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="text-white font-semibold">Coach IA Illimité</h3>
                <p className="text-gray-400 text-sm">Entraînements écrits et vocaux sans limite</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <svg className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="text-white font-semibold">Scénarios IA Immersifs</h3>
                <p className="text-gray-400 text-sm">Simulations réalistes avec feedback personnalisé</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <svg className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="text-white font-semibold">Suivi de Progression</h3>
                <p className="text-gray-400 text-sm">Tableaux de bord et analytics détaillés</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <svg className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="text-white font-semibold">Contenu Executive</h3>
                <p className="text-gray-400 text-sm">Frameworks et cas d'études de grandes entreprises</p>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <button
            onClick={handleSubscribe}
            disabled={loading}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold py-4 px-8 rounded-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Chargement...
              </span>
            ) : (
              'Commencer l\'essai gratuit (1 jour)'
            )}
          </button>

          {error && (
            <div className="mt-4 bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <p className="text-center text-gray-400 text-sm mt-4">
            🔒 Paiement 100% sécurisé par Stripe • Annulation à tout moment
          </p>
        </div>
        )}

        {/* Back Button */}
        <div className="text-center mt-8">
          <button
            onClick={handleBack}
            className="text-gray-400 hover:text-white transition-colors"
          >
            ← Retour au tableau de bord
          </button>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionPage;
